import * as THREE from 'three'
import Experience from '../Experience.js'
import Debug from '../Utils/Debug.js'
import Lights from './Lights.js'
import { vertexShader, fragmentShader } from './StarShader.js'
import Moon from './Moon.js'

export default class World {
    constructor() {
        this.experience = new Experience()
        this.scene = this.experience.scene
        this.sizes = this.experience.sizes
        this.debug = new Debug()
        this.moon = new Moon()

        // Debug GUI parameters
        this.parameters = {
            materialColor: '#ffffff'
        }

        this.lights = new Lights()

        this.setObjects()
        this.setParticles()
        this.setupDebugGUI()
    }

    setObjects() {
        // Texture
        const textureLoader = new THREE.TextureLoader()
        const gradientTexture = textureLoader.load('../textures/gradients/3.jpg')
        gradientTexture.magFilter = THREE.NearestFilter

        // Material
        this.material = new THREE.MeshToonMaterial({
            color: this.parameters.materialColor, // Use the parameter color
            gradientMap: gradientTexture
        })

        // Objects
        this.objectsDistance = 4
        this.mesh1 = new THREE.Mesh(
            new THREE.TorusGeometry(1, 0.4, 16, 60),
            this.material
        )
        this.mesh2 = new THREE.Mesh(
            new THREE.ConeGeometry(1, 2, 32),
            this.material
        )
        this.mesh3 = new THREE.Mesh(
            new THREE.TorusKnotGeometry(0.8, 0.35, 100, 16),
            this.material
        )

        // Wireframes
        const wireframeMaterial = new THREE.MeshBasicMaterial({ 
            color: 0xffffff, 
            wireframe: true, 
            transparent: true, 
            opacity: 0.1 
        })

        this.mesh1.add(new THREE.Mesh(this.mesh1.geometry, wireframeMaterial))
        this.mesh2.add(new THREE.Mesh(this.mesh2.geometry, wireframeMaterial))
        this.mesh3.add(new THREE.Mesh(this.mesh3.geometry, wireframeMaterial))

        this.updateObjectPositions()
        this.sizes.on('resize', () => this.updateObjectPositions())

        this.mesh1.position.y = -this.objectsDistance * 0
        this.mesh2.position.y = -this.objectsDistance * 1
        this.mesh3.position.y = -this.objectsDistance * 2

        this.scene.add(this.mesh1, this.mesh2, this.mesh3)
    }

    setParticles() {
        const particlesCount = 1000 // Increased count
        const positions = new Float32Array(particlesCount * 3)
        const scales = new Float32Array(particlesCount)
        const randomness = new Float32Array(particlesCount * 3)

        for (let i = 0; i < particlesCount; i++) {
            positions[i * 3 + 0] = (Math.random() - 0.5) * 10
            positions[i * 3 + 1] = this.objectsDistance * 0.5 - Math.random() * this.objectsDistance * 3
            positions[i * 3 + 2] = (Math.random() - 0.5) * 10
            scales[i] = Math.random()
            
            randomness[i * 3 + 0] = Math.random()
            randomness[i * 3 + 1] = Math.random()
            randomness[i * 3 + 2] = Math.random()
        }

        const particlesGeometry = new THREE.BufferGeometry()
        particlesGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3))
        particlesGeometry.setAttribute('aScale', new THREE.BufferAttribute(scales, 1))
        particlesGeometry.setAttribute('aRandomness', new THREE.BufferAttribute(randomness, 3))

        // Shader Material
        this.particlesMaterial = new THREE.ShaderMaterial({
            vertexShader: vertexShader,
            fragmentShader: fragmentShader,
            transparent: true,
            vertexColors: true,
            uniforms: {
                uTime: { value: 0 },
                uPixelRatio: { value: Math.min(window.devicePixelRatio, 2) }
            },
            blending: THREE.AdditiveBlending,
            depthWrite: false
        })

        // Points
        this.particles = new THREE.Points(particlesGeometry, this.particlesMaterial)
        this.scene.add(this.particles)
    }

    setupDebugGUI() {
        if (this.debug.ui) {
            this.debug.ui
                .addColor(this.parameters, 'materialColor')
                .onChange(() => {
                    this.material.color.set(this.parameters.materialColor)
                })
        }
    }

    updateObjectPositions() {
        // Calculate responsive X offset based on viewport width
        const aspectRatio = this.sizes.width / this.sizes.height
        const desktopAspectRatio = 16 / 9
        const xScale = Math.min(1, aspectRatio / desktopAspectRatio)
        
        this.mesh1.position.x = 2 * xScale
        this.mesh2.position.x = -2 * xScale
        this.mesh3.position.x = 2 * xScale
        
        // Also update moon position
        if (this.moon.moon) {
            this.moon.moon.position.x = 2 * xScale
        }
    }

    update() {
        if(this.particlesMaterial)
            this.particlesMaterial.uniforms.uTime.value = this.experience.time.elapsed
            
        // Rotate particles slowly
        if(this.particles)
            this.particles.rotation.y = this.experience.time.elapsed * 0.05
    }
}